import { useCallback, useState } from 'react';

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { RoleSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { useLoadState } from '../../hooks/useLoadState.ts';
import { RoleFormDialog } from './RoleFormDialog.tsx';
import { RolePermissionsEditor } from './RolePermissionsEditor.tsx';

export function RolesPage(): React.JSX.Element {
  const { api } = useAuth();

  const fetchRoles = useCallback(async () => (await api.roles.list()).roles, [api]);
  const { data: roles, state, reload } = useLoadState(fetchRoles, [] as RoleSummary[]);

  const [addOpen, setAddOpen] = useState(false);
  const [permsTarget, setPermsTarget] = useState<RoleSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleSummary | null>(null);

  const handleSaved = async (id: number): Promise<void> => {
    const fresh = await reload();
    setPermsTarget(fresh?.find((r) => r.id === id) ?? null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Roles</Typography>
        <Button onClick={() => setAddOpen(true)}>
          Add role
        </Button>
      </Box>

      {state.kind === 'loading' && <LoadingRow label="Loading roles..." />}

      {state.kind === 'error' && <LoadErrorAlert message={state.message} />}

      {state.kind === 'ready' && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.length === 0 && <EmptyRow colSpan={3} message="No roles yet." />}
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700 }}>{role.name}</Typography>
                        {role.is_system && (
                          <Tooltip title="System role -- cannot be modified">
                            <LockIcon fontSize="small" aria-label="locked" sx={{ opacity: 0.7 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {role.permissions.map((p) => (
                          <Chip key={p.id} label={p.code} size="small" />
                        ))}
                        {role.permissions.length === 0 && (
                          <Typography variant="body2" sx={{ opacity: 0.6 }}>
                            None
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {role.is_system ? (
                        <Typography variant="body2" sx={{ opacity: 0.6 }}>
                          Locked
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <QuietButton size="small" onClick={() => setPermsTarget(role)}>
                            Edit permissions
                          </QuietButton>
                          <Button size="small" variant="outlined" color="error" onClick={() => setDeleteTarget(role)}>
                            Delete
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <RoleFormDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={(id) => void handleSaved(id)} />

      <RolePermissionsEditor
        open={permsTarget !== null}
        role={permsTarget}
        onClose={() => setPermsTarget(null)}
        onSaved={() => void reload()}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete role"
        body={deleteTarget ? `Delete the "${deleteTarget.name}" role?` : ''}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={async () => {
          if (deleteTarget) await api.roles.remove(deleteTarget.id);
        }}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => void reload()}
      />
    </Box>
  );
}

export default RolesPage;
